import React, { Fragment } from 'react';
import { QueryClientProvider, QueryClient } from 'react-query';
import PropTypes from 'prop-types';
import CSProvider from '@clearscore/redux.cs-provider';
import Font from '@clearscore/rainbow.font';
import initialiseTrackingCookies from '@clearscore/helpers.initialise-tracking-cookies';

import { useCoreVisitTracking } from './lib/hooks/use-tracking';
import Router from './core-router';
import './lib/a11y-outline';
// NOTE: `reset` should go before any other styles, otherwise it will brake fonts
//       and you will have specificity wars.
import './styles/reset.css';
import './styles/index.css';

const queryClient = new QueryClient();

const CoreRouter = ({ store, storeConfig, templates, hasPageTracking }) => {
    const { onFirstTimeVisit, onMarketingCampaignVisit } = useCoreVisitTracking();

    initialiseTrackingCookies(store, null, { onFirstTimeVisit, onMarketingCampaignVisit });
    const parentState = store.getState();

    return (
        <Fragment>
            <Font />
            <Router
                templates={templates}
                parentState={parentState}
                coreAppBridgeTypes={storeConfig.appBridgeTypes}
                hasPageTracking={hasPageTracking}
            />
        </Fragment>
    );
};

const CoreApp = ({ storeConfig, templates, hasPageTracking }) => (
    <QueryClientProvider client={queryClient}>
        <CSProvider storeConfig={storeConfig}>
            {({ store }) => (
                <CoreRouter
                    store={store}
                    storeConfig={storeConfig}
                    templates={templates}
                    hasPageTracking={hasPageTracking}
                />
            )}
        </CSProvider>
    </QueryClientProvider>
);

CoreRouter.propTypes = {
    store: PropTypes.object.isRequired,
    /** @type {Object} config use to configure redux store, including reducers, initial state etc. */
    storeConfig: PropTypes.object.isRequired,
    /** @type {Object} mapping of config template string to template component */
    templates: PropTypes.object.isRequired,
    hasPageTracking: PropTypes.bool,
};

CoreRouter.defaultProps = {
    hasPageTracking: false,
};

CoreApp.propTypes = {
    /** @type {Object} config use to configure redux store, including reducers, initial state etc. */
    storeConfig: PropTypes.object.isRequired,
    /** @type {Object} mapping of config template string to template component */
    templates: PropTypes.object.isRequired,
    hasPageTracking: PropTypes.bool,
};

CoreApp.defaultProps = {
    hasPageTracking: false,
};

export default CoreApp;
